import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { server } from "../server";
import { Filter, ChevronLeft, ChevronRight, User, Shield, CheckCircle, Clock, Edit, X, Save, Building2, Key, FileText, Phone, Briefcase, Lock } from "lucide-react";
import Skeleton from "../Components/SkeletonLoader";

export default function UserListPage() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pageNo, setPageNo] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [userTypeFilter, setUserTypeFilter] = useState("");
  const [uniqueUserTypes, setUniqueUserTypes] = useState([]);

  // Edit Modal State
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editFormData, setEditFormData] = useState({
      fullName: "",
      designation: "",
      mobileNo: "",
      userType: "",
      status: true,
      vlanId: 0,
      department: "",
      policy: "",
      key: "",
      isReadOnly: false,
      password: "" // Optional if needed, but not in payload provided in prompt, removing for safety unless requested
  });
  const [departmentsList, setDepartmentsList] = useState([]);
  const [isUpdating, setIsUpdating] = useState(false);

  const userTypes = ["Minister", "Secretary", "Director", "Others"];

  useEffect(() => {
    const fetchDepartments = async () => {
      try {
        const token = localStorage.getItem("dbToken");
        const response = await axios.get(`${server}/department/getAll`, {
          headers: { Authorization: `Bearer ${token}` },
          params: { pageNo: 1, pageSize: 1000 }
        });
        if (response.data.success) {
          setDepartmentsList(response.data.data);
        }
      } catch (error) {
        console.error("Error fetching departments:", error);
      }
    };
    fetchDepartments();
  }, []);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("dbToken");
      const params = {
        pageNo,
        pageSize,
      };
      if (userTypeFilter) {
        params.userType = userTypeFilter;
      }

      await axios
        .get(`${server}/auth/getAllUsers`, {
          params,
          headers: {
            Authorization: `Bearer ${token}`,
          },
        })
        .then((response) => {
          if (response.data.success) {
            setUsers(response.data.data);
            setTotalCount(response.data.count);
            if (response.data.uniqueUserTypes) {
                setUniqueUserTypes(response.data.uniqueUserTypes);
            }
          } else {
            toast.error("Failed to fetch users");
          }
        });
    } catch (error) {
      console.error("Error fetching users:", error);
      toast.error(error.response?.data?.message || "Error fetching users");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, [pageNo, pageSize, userTypeFilter]);

  const handlePageChange = (newPage) => {
    if (newPage > 0 && newPage <= Math.ceil(totalCount / pageSize)) {
      setPageNo(newPage);
    }
  };

  const handleEditClick = (user) => {
      setEditFormData({
          id: user.id || user._id, // Ensure we have ID for reference if needed, though API might not use it in body based on prompt
          fullName: user.fullName || "",
          designation: user.designation || "",
          mobileNo: user.mobileNo || "",
          userType: user.userType || "",
          status: user.status !== undefined ? user.status : true,
          vlanId: user.vlanId || 0,
          department: user.department || user.receivedDepartment || "", // Handle potential field name mismatch
          policy: user.policy || "",
          key: user.key || "",
          isReadOnly: user.isReadOnly || false
      });
      setIsEditModalOpen(true);
  };

  const handleEditChange = (e) => {
      const { name, value, type, checked } = e.target;
      setEditFormData(prev => ({
          ...prev,
          [name]: type === 'checkbox' ? checked : value
      }));
  };

  const handleDepartmentChange = (e) => {
      const deptName = e.target.value;
      const deptObj = departmentsList.find((d) => d.departmentName === deptName);
      
      setEditFormData(prev => ({
          ...prev,
          department: deptName,
          vlanId: deptObj ? deptObj.vlanId : prev.vlanId
      }));
  };

  
  const handleUserTypeChange = (e) => {
      const value = e.target.value;
      setEditFormData((prev) => ({
        ...prev,
        userType: value,
        vlanId: value !== "Others" ? "20" : prev.vlanId,
        department: value !== "Others" ? "Admin" : prev.department,
      }));
  };

  const handleUpdateUser = async (e) => {
      e.preventDefault();
      setIsUpdating(true);

      try {
          const token = localStorage.getItem("dbToken");
          const payload = {
              ...editFormData,
              mobileNo: Number(editFormData.mobileNo),
              vlanId: Number(editFormData.vlanId)
          };
          // Remove ID from payload if it's there as per request body
          delete payload.id; 
          delete payload.password; // clean up just in case

          const response = await axios.patch(`${server}/auth/updateBasicInfo`, payload, {
              headers: { Authorization: `Bearer ${token}` }
          });

          if (response.data.success || response.status === 200) {
              toast.success("User updated successfully");
              setIsEditModalOpen(false);
              fetchUsers(); // Refresh list
          } else {
              toast.error("Failed to update user");
          }
      } catch (error) {
          console.error("Update error:", error);
          toast.error(error.response?.data?.message || "Failed to update user");
      } finally {
          setIsUpdating(false);
      }
  };

  const totalPages = Math.ceil(totalCount / pageSize);

  if (loading && users.length === 0) {
      return (
          <div className="p-8 flex items-center justify-center text-gray-500">
             <div className="space-y-4 w-full max-w-4xl">
                 <Skeleton className="h-8 w-48 mb-6" />
                 <div className="space-y-4">
                     {[1, 2, 3, 4, 5].map(i => <Skeleton key={i} className="h-16 w-full rounded-xl" />)}
                 </div>
             </div>
          </div>
      );
  }

  return (
    <div className="p-6 max-w-[1600px] mx-auto space-y-6">
      {/* Header Section */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
              <h1 className="text-2xl font-bold text-gray-900 tracking-tight">User Management</h1>
              <p className="text-sm text-gray-500 mt-1">Manage system users, roles, and permissions.</p>
          </div>

          <div className="flex items-center gap-4">
             {/* Filter Control */}
             <div className="relative group">
                 <Filter className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 group-hover:text-blue-500 transition-colors" />
                 <select
                    value={userTypeFilter}
                    onChange={(e) => {
                        setUserTypeFilter(e.target.value);
                        setPageNo(1);
                    }}
                    className="pl-9 pr-10 py-2.5 bg-white border border-gray-200 rounded-lg text-sm font-medium text-gray-700 hover:border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-400 transition-all cursor-pointer appearance-none min-w-[180px]"
                 >
                    <option value="">All Roles</option>
                    {uniqueUserTypes.map((type) => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                 </select>
             </div>
          </div>
      </div>

      {/* Main Content */}
      <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden flex flex-col">
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-gray-50/50 border-b border-gray-200 text-xs font-bold text-gray-500 uppercase tracking-wider">
                <th className="px-6 py-4">User Profile</th>
                <th className="px-6 py-4">Contact</th>
                <th className="px-6 py-4">Policy / Key</th>
                <th className="px-6 py-4">Account Status</th>
                <th className="px-6 py-4">Joined Date</th>
                <th className="px-6 py-4">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
               {users.length === 0 ? (
                <tr>
                   <td colSpan="5" className="px-6 py-16 text-center text-gray-500">
                      <div className="flex flex-col items-center justify-center gap-2">
                          <div className="p-3 bg-gray-50 rounded-full">
                              <User className="h-6 w-6 text-gray-300" />
                          </div>
                          <span className="text-sm font-medium">No users found</span>
                      </div>
                   </td>
                </tr>
               ) : (
                users.map((user) => (
                  <tr key={user.id} className="group hover:bg-blue-50/30 transition-colors">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                          <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center text-blue-700 font-bold text-sm shrink-0 border border-blue-200">
                             {user.fullName ? user.fullName.charAt(0).toUpperCase() : 'U'}
                          </div>
                          <div>
                              <div className="font-bold text-gray-900 text-sm">{user.fullName || 'Unknown User'}</div>
                              <div className="flex items-center gap-1 mt-0.5">
                                <Shield className="h-3 w-3 text-blue-500" />
                                <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">{user.userType || "N/A"}</span>
                              </div>
                          </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                       {user.mobileNo ? (
                           <div className="flex items-center gap-2 text-sm text-gray-600 font-medium">
                                <Clock className="h-4 w-4 text-gray-400 hidden" /> {/* Hidden icon to keep alignment if needed, or just use Phone */}
                                <span>{user.mobileNo}</span>
                           </div>
                       ) : (
                           <span className="text-xs text-gray-400">—</span>
                       )}
                    </td>
                    <td className="px-6 py-4">
                        <div className="flex flex-col gap-1.5 items-start">
                            {user.policy && (
                                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-50 text-purple-700 border border-purple-100">
                                    {user.policy}
                                </span>
                            )}
                            {user.key && (
                                <div className="flex items-center gap-1.5 text-xs text-gray-500 font-mono bg-gray-50 px-1.5 py-0.5 rounded border border-gray-200">
                                    <span className="text-gray-400">#</span>
                                    {user.key}
                                </div>
                            )}
                        </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex flex-col gap-2 items-start">
                          {/* Verified Status */}
                          {user.verified ? (
                             <div className="flex items-center gap-1.5 text-xs font-bold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full border border-emerald-100">
                                <CheckCircle className="h-3 w-3 fill-emerald-500 text-emerald-500" />
                                <span>Verified</span>
                             </div>
                          ) : (
                             <div className="flex items-center gap-1.5 text-xs font-bold text-amber-700 bg-amber-50 px-2 py-0.5 rounded-full border border-amber-100">
                                <div className="h-3 w-3 rounded-full border-2 border-amber-500 flex items-center justify-center">
                                    <span className="h-1 w-1 rounded-full bg-amber-500"></span>
                                </div>
                                <span>Unverified</span>
                             </div>
                          )}

                          {/* Active Status */}
                          <div className={`flex items-center gap-1.5 text-xs font-medium ${user.status ? 'text-gray-700' : 'text-gray-400'}`}>
                              <span className={`h-2 w-2 rounded-full ${user.status ? 'bg-green-500 shadow-[0_0_0_2px_rgba(34,197,94,0.2)]' : 'bg-gray-300'}`}></span>
                              {user.status ? "Active" : "Inactive"}
                          </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                       <div className="text-sm text-gray-500 font-medium">
                           {user.createdAt ? new Date(user.createdAt).toLocaleDateString() : '—'}
                       </div>
                       <div className="text-xs text-gray-400 mt-0.5">
                           {user.createdAt ? new Date(user.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : ''}
                       </div>
                    </td>
                    <td className="px-6 py-4">
                        <button 
                            onClick={() => handleEditClick(user)}
                            className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                            title="Edit User"
                        >
                            <Edit className="h-4 w-4" />
                        </button>
                    </td>
                  </tr>
                ))
               )}
            </tbody>
          </table>
        </div>

        {/* Pagination Footer */}
        {totalCount > 0 && (
            <div className="bg-gray-50 border-t border-gray-200 px-6 py-4 flex flex-col sm:flex-row justify-between items-center gap-4">
              <div className="text-xs font-medium text-gray-500">
                Showing <span className="text-gray-900 font-bold">{users.length}</span> of <span className="text-gray-900 font-bold">{totalCount}</span> users
              </div>
              
              <div className="flex items-center gap-2">
                <button
                  onClick={() => handlePageChange(pageNo - 1)}
                  disabled={pageNo === 1}
                  className="p-2 rounded-lg bg-white border border-gray-200 hover:bg-gray-50 text-gray-600 disabled:opacity-40 disabled:cursor-not-allowed transition-all shadow-sm"
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>
                <div className="px-4 py-1.5 rounded-lg bg-white border border-gray-200 text-xs font-mono font-medium text-gray-700 shadow-sm">
                  Page {pageNo} of {totalPages || 1}
                </div>
                <button
                  onClick={() => handlePageChange(pageNo + 1)}
                  disabled={pageNo === totalPages || totalPages === 0}
                  className="p-2 rounded-lg bg-white border border-gray-200 hover:bg-gray-50 text-gray-600 disabled:opacity-40 disabled:cursor-not-allowed transition-all shadow-sm"
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            </div>
        )}
      </div>
      
      {/* Edit User Modal */}
      {isEditModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 animate-in fade-in">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
                <div className="flex items-center justify-between p-6 border-b border-gray-100 sticky top-0 bg-white z-10">
                    <div>
                        <h2 className="text-xl font-bold text-gray-900">Edit User Details</h2>
                        <p className="text-sm text-gray-500">Update user information and permissions</p>
                    </div>
                    <button 
                        onClick={() => setIsEditModalOpen(false)}
                        className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                        <X className="h-5 w-5" />
                    </button>
                </div>
                
                <form onSubmit={handleUpdateUser} className="p-6 space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* Full Name */}
                        <div className="space-y-2">
                             <label className="text-xs font-bold text-gray-500 uppercase tracking-wider">Full Name</label>
                             <div className="relative">
                                 <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                                 <input
                                     type="text"
                                     name="fullName"
                                     value={editFormData.fullName}
                                     onChange={handleEditChange}
                                     className="w-full pl-9 pr-4 py-2.5 bg-white border border-gray-200 rounded-lg text-sm font-medium text-gray-900 focus:ring-2 focus:ring-blue-100 focus:border-blue-400 outline-none transition-all placeholder-gray-400"
                                     placeholder="Full Name"
                                     required
                                 />
                             </div>
                        </div>

                         {/* Mobile */}
                         <div className="space-y-2">
                             <label className="text-xs font-bold text-gray-500 uppercase tracking-wider">Mobile No</label>
                             <div className="relative">
                                 <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                                 <input
                                     type="number"
                                     name="mobileNo"
                                     value={editFormData.mobileNo}
                                     onChange={handleEditChange}
                                     className="w-full pl-9 pr-4 py-2.5 bg-white border border-gray-200 rounded-lg text-sm font-medium text-gray-900 focus:ring-2 focus:ring-blue-100 focus:border-blue-400 outline-none transition-all placeholder-gray-400"
                                     placeholder="Mobile No"
                                     required
                                 />
                             </div>
                        </div>

                        {/* Designation */}
                        <div className="space-y-2">
                             <label className="text-xs font-bold text-gray-500 uppercase tracking-wider">Designation</label>
                             <div className="relative">
                                 <Briefcase className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                                 <input
                                     type="text"
                                     name="designation"
                                     value={editFormData.designation}
                                     onChange={handleEditChange}
                                     className="w-full pl-9 pr-4 py-2.5 bg-white border border-gray-200 rounded-lg text-sm font-medium text-gray-900 focus:ring-2 focus:ring-blue-100 focus:border-blue-400 outline-none transition-all placeholder-gray-400"
                                     placeholder="Designation"
                                 />
                             </div>
                        </div>

                        {/* User Type */}
                        <div className="space-y-2">
                             <label className="text-xs font-bold text-gray-500 uppercase tracking-wider">User Type</label>
                             <div className="relative">
                                 <Shield className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                                 <select
                                     name="userType"
                                     value={editFormData.userType}
                                     onChange={handleUserTypeChange}
                                     className="w-full pl-9 pr-4 py-2.5 bg-white border border-gray-200 rounded-lg text-sm font-medium text-gray-900 focus:ring-2 focus:ring-blue-100 focus:border-blue-400 outline-none transition-all appearance-none cursor-pointer"
                                     required
                                 >
                                      <option value="" disabled>Select Type</option>
                                      {userTypes.map(t => <option key={t} value={t}>{t}</option>)}
                                 </select>
                             </div>
                        </div>

                        {/* Department */}
                        <div className="space-y-2">
                             <label className="text-xs font-bold text-gray-500 uppercase tracking-wider">Department</label>
                             <div className="relative">
                                 <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                                 {editFormData.userType === "Others" ? (
                                     <select
                                        name="department"
                                        value={editFormData.department}
                                        onChange={handleDepartmentChange} // Use specific handler
                                        className="w-full pl-9 pr-4 py-2.5 bg-white border border-gray-200 rounded-lg text-sm font-medium text-gray-900 focus:ring-2 focus:ring-blue-100 focus:border-blue-400 outline-none transition-all appearance-none cursor-pointer"
                                        required
                                     >
                                         <option value="" disabled>Select Department</option>
                                         {departmentsList.map((dept, idx) => (
                                             <option key={idx} value={dept.departmentName}>{dept.departmentName}</option>
                                         ))}
                                     </select>
                                 ) : (
                                     <input
                                        type="text"
                                        value="Admin"
                                        readOnly
                                        className="w-full pl-9 pr-4 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-sm font-medium text-gray-500 cursor-not-allowed"
                                     />
                                 )}
                             </div>
                        </div>
                        
                         {/* VLAN ID */}
                         {editFormData.userType === "Others" && (
                         <div className="space-y-2">
                             <label className="text-xs font-bold text-gray-500 uppercase tracking-wider">VLAN ID</label>
                             <div className="relative">
                                 <input
                                     type="number"
                                     name="vlanId"
                                     value={editFormData.vlanId}
                                     readOnly
                                     className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-sm font-mono font-medium text-gray-500 cursor-not-allowed focus:outline-none"
                                 />
                             </div>
                        </div>
                        )}

                        {/* Policy */}
                        <div className="space-y-2">
                             <label className="text-xs font-bold text-gray-500 uppercase tracking-wider">Policy</label>
                             <div className="relative">
                                 <FileText className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                                 <input
                                     type="text"
                                     name="policy"
                                     value={editFormData.policy}
                                     onChange={handleEditChange}
                                     className="w-full pl-9 pr-4 py-2.5 bg-white border border-gray-200 rounded-lg text-sm font-medium text-gray-900 focus:ring-2 focus:ring-blue-100 focus:border-blue-400 outline-none transition-all placeholder-gray-400"
                                     placeholder="Policy"
                                 />
                             </div>
                        </div>

                         {/* Key */}
                         <div className="space-y-2">
                             <label className="text-xs font-bold text-gray-500 uppercase tracking-wider">Key</label>
                             <div className="relative">
                                 <Key className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                                 <input
                                     type="text"
                                     name="key"
                                     value={editFormData.key}
                                     onChange={handleEditChange}
                                     className="w-full pl-9 pr-4 py-2.5 bg-white border border-gray-200 rounded-lg text-sm font-medium text-gray-900 focus:ring-2 focus:ring-blue-100 focus:border-blue-400 outline-none transition-all placeholder-gray-400"
                                     placeholder="Key"
                                 />
                             </div>
                        </div>

                        {/* Status Checkbox */}
                         <div className="space-y-2">
                            <label className="text-xs font-bold text-gray-500 uppercase tracking-wider block">Account Status</label>
                            <div className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer border-gray-200 hover:bg-gray-50`} onClick={() => setEditFormData(prev => ({...prev, status: !prev.status}))}>
                                <div className={`w-5 h-5 rounded-full flex items-center justify-center transition-colors ${editFormData.status ? 'bg-green-500' : 'bg-gray-300'}`}>
                                    {editFormData.status && <CheckCircle className="h-3 w-3 text-white" />}
                                </div>
                                <span className="text-sm font-medium text-gray-700">{editFormData.status ? "Active" : "Inactive"}</span>
                            </div>
                         </div>

                        {/* Read Only Checkbox */}
                         <div className="space-y-2">
                            <label className="text-xs font-bold text-gray-500 uppercase tracking-wider block">Access Level</label>
                            <label className="flex items-center gap-3 p-3 rounded-lg border border-gray-200 hover:bg-gray-50 cursor-pointer">
                                <input
                                    type="checkbox"
                                    name="isReadOnly"
                                    checked={editFormData.isReadOnly}
                                    onChange={handleEditChange}
                                    className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                                />
                                <span className="text-sm font-medium text-gray-700">Is Read Only User</span>
                            </label>
                         </div>

                    </div>
                    
                    <div className="pt-6 border-t border-gray-100 flex justify-end gap-3">
                        <button
                            type="button"
                            onClick={() => setIsEditModalOpen(false)}
                            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={isUpdating}
                            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 active:bg-blue-800 transition-all shadow-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                        >
                            {isUpdating ? (
                                <div className="h-4 w-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                            ) : (
                                <Save className="h-4 w-4" />
                            )}
                            Save Changes
                        </button>
                    </div>
                </form>
            </div>
        </div>
      )}
    </div>
  );
}
